/**
 * useCommandRegistry — the client-side half of the Cmd-K palette.
 *
 * Assembles a {@link CommandContext} from the same visibility hooks the rest of
 * the app already trusts (workspace, PHI mode, viewer role, knowledge-graph
 * entitlement) and runs the pure scorer from `constants/commandRegistry` against
 * the current query. Everything here is synchronous client compute — no
 * TanStack Query, no network — so the palette paints instant "Go to" / Settings
 * matches while the server hybrid-search round-trip is still in flight.
 *
 * The `top` entry (a strong exact/prefix hit) is pulled out of the grouped
 * lists so the UI can render it once as the highlighted "Top result" without it
 * reappearing under Go to / Settings.
 */

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import {
  COMMAND_ENTRIES,
  scoreCommands,
  pickTopCommand,
  type CommandContext,
  type CommandEntry,
} from '../../constants/commandRegistry';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useIsPhiWorkspace } from '../user/useIsPhiWorkspace';
import { useIsReadOnly } from '../user/useWorkspaceRole';
import { useCanUseFeature } from '../ui/useFeatureGate';

export interface CommandRegistryResult {
  /** The single strong match, or null when no exact/prefix hit. */
  readonly top: CommandEntry | null;
  /** Scored 'goto' entries, best-first, excluding the top entry. */
  readonly goto: readonly CommandEntry[];
  /** Scored 'settings' entries, best-first, excluding the top entry. */
  readonly settings: readonly CommandEntry[];
  /** Help-centre entries; each opens docs.bowerlabs.ai in a new tab. */
  readonly help: readonly CommandEntry[];
  /** Every visible entry per group, unscored — for browsing a scope with no query. */
  readonly browse: {
    readonly goto: readonly CommandEntry[];
    readonly settings: readonly CommandEntry[];
    readonly help: readonly CommandEntry[];
  };
  /** Run an entry's action against the live context. Stable identity. */
  readonly perform: (entry: CommandEntry) => void;
}

export function useCommandRegistry(query: string): CommandRegistryResult {
  const { workspace } = useWorkspace();
  const isPhiWorkspace = useIsPhiWorkspace();
  const isReadOnly = useIsReadOnly();
  // Passive visibility check — a click-time signal fires the intent event.
  const { allowed: graphEnabled } = useCanUseFeature('knowledgeGraph', { track: false });
  const navigate = useNavigate();

  const ctx = useMemo<CommandContext>(
    () => ({ workspace, isPhiWorkspace, isReadOnly, graphEnabled, navigate }),
    [workspace, isPhiWorkspace, isReadOnly, graphEnabled, navigate],
  );

  // Everything visible in this context, UNSCORED. A specific scope with an
  // empty query is a BROWSE ("show me the settings"), not a search — scoring
  // returns [] for an empty query, which made Navigation / Settings / Help
  // look broken the moment you clicked them.
  const browse = useMemo(() => {
    const visible = COMMAND_ENTRIES.filter((entry) => entry.visible(ctx));
    return {
      goto: visible.filter((e) => e.group === 'goto'),
      settings: visible.filter((e) => e.group === 'settings'),
      help: visible.filter((e) => e.group === 'help'),
    };
  }, [ctx]);

  const { top, goto, settings, help } = useMemo(() => {
    // Score once; the sorted list gives both the Top result (best, if strong
    // enough) and the grouped lists — no second scoring pass.
    const scored = scoreCommands(query, ctx);
    const best = pickTopCommand(scored);
    const visibleEntries = scored.map((s) => s.entry).filter((entry) => entry.id !== best?.id);
    return {
      top: best,
      goto: visibleEntries.filter((entry) => entry.group === 'goto'),
      settings: visibleEntries.filter((entry) => entry.group === 'settings'),
      help: visibleEntries.filter((entry) => entry.group === 'help'),
    };
  }, [query, ctx]);

  const perform = useCallback((entry: CommandEntry) => entry.perform(ctx), [ctx]);

  return { top, goto, settings, help, browse, perform };
}
